const HIDDEN_MARK = 'data-a11y-modal-hidden'
const SKIP_TAGS = ['SCRIPT', 'STYLE', 'LINK', 'META', 'TEMPLATE']

/**
 * 모달이 열려 있는 동안 배경(모달 바깥 영역)을 스크린리더에서 감춘다.
 *
 * 반투명 오버레이는 마우스만 막아줄 뿐이고, aria-modal 속성은 브라우저/스크린리더 조합에 따라
 * 배경이 계속 읽히는 경우가 있다. 그래서 모달 요소의 조상 체인을 따라 올라가며
 * 형제 요소에 aria-hidden 을 직접 건다.
 *
 * aria-hidden 을 스스로 관리하는 요소(속성이 이미 있는 요소)는 값이 무엇이든 건드리지 않는다.
 * 예전에는 "false" 인 요소도 "true" 로 바꾸고 닫을 때 "false" 로 되돌렸는데, 그 사이 요소의
 * 주인(Vue 바인딩)이 값을 바꿔도 되돌리기가 그것을 덮어썼다. 간편모드 로딩 오버레이(EasyLoading)는
 * aria-hidden 하나로 표시가 결정되는데, API 요청 중(false) 에 "기기 연결" 확인창이 열리고 요청이
 * 끝난 뒤(true) 확인창을 닫으면 되돌리기가 다시 "false" 를 써서 오버레이가 켜진 채 남았다
 * (첫 진입 "무한 로딩"). 속성이 없던 요소만 감추고 되돌리면 이 충돌이 생기지 않는다.
 *
 * @param {HTMLElement} dialogEl 열린 모달의 최상위 요소
 * @returns {Function} 배경을 원래대로 되돌리는 함수 (여러 번 호출해도 안전)
 */
export function hideBackgroundFor(dialogEl) {
    const noop = () => {}
    if (!dialogEl || !dialogEl.parentElement) return noop

    const changed = []
    let node = dialogEl

    while (node && node.parentElement) {
        const parent = node.parentElement

        for (const sibling of Array.from(parent.children)) {
            if (sibling === node) continue
            if (SKIP_TAGS.includes(sibling.tagName)) continue
            // aria-hidden 을 이미 가진 요소는 그 주인이 관리한다. "true" 면 다른 모달이 감췼거나
            // 원래 그런 요소이고, "false" 면 일부러 드러낸 요소(로딩 오버레이 등)다. 둘 다 손대지 않는다.
            // 여기서 바꾸면 닫을 때 되돌리는 값이 그 사이 바뀐 주인의 값을 덮어쓴다.
            if (sibling.hasAttribute('aria-hidden')) continue

            changed.push(sibling)
            sibling.setAttribute('aria-hidden', 'true')
            sibling.setAttribute(HIDDEN_MARK, '')
        }

        if (parent === document.body) break
        node = parent
    }

    let restored = false
    return () => {
        if (restored) return
        restored = true

        changed.forEach((el) => {
            // 우리가 건 표시가 남아 있을 때만 되돌린다. 그 사이 누군가 aria-hidden 을 직접 쓰기
            // 시작했다면(표시는 그대로여도 값이 달라졌다면) 그 값이 주인의 뜻이다.
            const stillOurs = el.hasAttribute(HIDDEN_MARK) && el.getAttribute('aria-hidden') === 'true'
            el.removeAttribute(HIDDEN_MARK)
            if (stillOurs) {
                el.removeAttribute('aria-hidden')
            }
        })
        changed.length = 0
    }
}
